<template>
  <a href="#main" class="skip-link">Skip to content</a>
  <header
    ref="header"
    class="nav"
    :class="{ 'nav--solid': solid || scrolled, 'nav--open': menuOpen }"
  >
    <div class="nav__inner container">
      <router-link
        to="/"
        class="nav__brand"
        aria-label="Allading home"
        @click="closeMenu"
      >
        <img :src="icon" alt="" class="nav__logo" width="28" height="28" />
        <span class="nav__name">Allading</span>
      </router-link>

      <nav class="nav__links" aria-label="Primary">
        <router-link v-for="link in links" :key="link.label" :to="link.to">{{
          link.label
        }}</router-link>
      </nav>

      <div class="nav__actions">
        <router-link :to="{ path: '/', hash: '#download' }" class="nav__cta"
          >Download</router-link
        >
        <button
          ref="toggle"
          type="button"
          class="nav__toggle"
          :aria-expanded="menuOpen"
          aria-controls="mobile-menu"
          :aria-label="menuOpen ? 'Close menu' : 'Open menu'"
          @click="menuOpen = !menuOpen"
        >
          <span class="nav__toggle-line"></span>
          <span class="nav__toggle-line"></span>
        </button>
      </div>
    </div>

    <transition name="menu">
      <div v-if="menuOpen" id="mobile-menu" ref="menu" class="menu">
        <nav class="menu__links container" aria-label="Mobile">
          <router-link
            v-for="link in links"
            :key="link.label"
            :to="link.to"
            @click="closeMenu"
          >
            {{ link.label }}
          </router-link>
          <router-link :to="{ path: '/', hash: '#download' }" @click="closeMenu"
            >Download</router-link
          >
        </nav>
        <div class="menu__footer container">
          <StoreButtons tone="dark" />
        </div>
      </div>
    </transition>
  </header>
</template>

<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import icon from "@/assets/allading-icon.png";
import StoreButtons from "./StoreButtons.vue";

withDefaults(defineProps<{ solid?: boolean }>(), { solid: false });

const links = [
  { label: "Features", to: { path: "/", hash: "#features" } },
  { label: "Security", to: { path: "/", hash: "#security" } },
  { label: "FAQ", to: { path: "/", hash: "#faq" } },
  { label: "Privacy & terms", to: { path: "/legal" } },
];

const scrolled = ref(false);
const menuOpen = ref(false);
const header = ref<HTMLElement>();
const toggle = ref<HTMLButtonElement>();
const menu = ref<HTMLElement>();

function onScroll() {
  scrolled.value = window.scrollY > 24;
}

function onKey(e: KeyboardEvent) {
  if (e.key === "Escape") menuOpen.value = false;
}

function closeMenu() {
  menuOpen.value = false;
}

// While the menu is open, page content behind it is inert so focus stays in the menu.
function setBackgroundInert(inert: boolean) {
  const siblings = header.value?.parentElement?.children ?? [];
  for (const el of Array.from(siblings)) {
    if (el !== header.value) el.toggleAttribute("inert", inert);
  }
}

watch(menuOpen, async (open) => {
  document.documentElement.style.overflow = open ? "hidden" : "";
  setBackgroundInert(open);
  await nextTick();
  if (open) menu.value?.querySelector<HTMLElement>("a")?.focus();
  else if (document.activeElement === document.body) toggle.value?.focus();
});

onMounted(() => {
  onScroll();
  window.addEventListener("scroll", onScroll, { passive: true });
  window.addEventListener("keydown", onKey);
});

onBeforeUnmount(() => {
  window.removeEventListener("scroll", onScroll);
  window.removeEventListener("keydown", onKey);
  document.documentElement.style.overflow = "";
  setBackgroundInert(false);
});
</script>

<style scoped>
.nav {
  position: fixed;
  inset: 0 0 auto;
  z-index: 50;
  color: #fff;
  transition: background-color 400ms var(--ease), color 400ms var(--ease),
    box-shadow 400ms var(--ease);
}

.nav--solid,
.nav--open {
  background: rgba(255, 255, 255, 0.88);
  -webkit-backdrop-filter: blur(16px) saturate(1.4);
  backdrop-filter: blur(16px) saturate(1.4);
  color: var(--n-900);
  box-shadow: 0 1px 0 var(--border-light);
}

/* backdrop-filter would make the nav the containing block of the fixed menu */
.nav--open {
  background: #fff;
  -webkit-backdrop-filter: none;
  backdrop-filter: none;
}

.nav__inner {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: var(--nav-h);
}

.nav__brand {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 44px;
}

.nav__logo {
  width: 28px;
  height: 28px;
  border-radius: 7px;
}

.nav__name {
  font-size: 16px;
  font-weight: 600;
  letter-spacing: -0.02em;
}

.nav__links {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 24px;
}

.nav__links a,
.nav__cta {
  font-size: 14px;
  letter-spacing: -0.01em;
  transition: opacity 180ms ease;
}

.nav__links a:hover {
  opacity: 0.6;
}

.nav__actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.nav__cta {
  display: inline-flex;
  align-items: center;
  height: 40px;
  padding: 0 20px;
  border-radius: 999px;
  font-weight: 500;
  background: #fff;
  color: var(--n-900);
  transition: background-color 400ms var(--ease), color 400ms var(--ease),
    transform 180ms ease;
}

.nav--solid .nav__cta,
.nav--open .nav__cta {
  background: var(--n-900);
  color: #fff;
}

.nav__cta:hover {
  transform: translateY(-1px);
}

.nav__toggle {
  display: none;
  position: relative;
  width: 44px;
  height: 44px;
  margin-right: -10px;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
}

.nav__toggle-line {
  position: absolute;
  left: 12px;
  width: 20px;
  height: 1.5px;
  background: currentColor;
  transition: transform 400ms var(--ease-out);
}

.nav__toggle-line:first-child {
  top: 18px;
}

.nav__toggle-line:last-child {
  top: 25px;
}

.nav--open .nav__toggle-line:first-child {
  transform: translateY(3.5px) rotate(45deg);
}

.nav--open .nav__toggle-line:last-child {
  transform: translateY(-3.5px) rotate(-45deg);
}

.menu {
  position: fixed;
  inset: var(--nav-h) 0 0;
  background: #fff;
  color: var(--n-900);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 24px 0 calc(32px + env(safe-area-inset-bottom));
  overflow-y: auto;
}

.menu__links {
  display: flex;
  flex-direction: column;
}

.menu__links a {
  padding: 18px 0;
  font-size: 32px;
  font-weight: 500;
  letter-spacing: -0.03em;
  line-height: 1.1;
  border-bottom: 1px solid var(--border-light);
}

.menu__footer :deep(.stores) {
  justify-content: flex-start;
}

.menu-enter-active,
.menu-leave-active {
  transition: opacity 400ms var(--ease-out), transform 400ms var(--ease-out);
}

.menu-enter-from,
.menu-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

@media (max-width: 767px) {
  .nav__links,
  .nav__cta {
    display: none;
  }
  .nav__toggle {
    display: block;
  }
}

@media (min-width: 768px) {
  .menu {
    display: none;
  }
}
</style>
